import { Button, Container, MultiselectProps } from '@amzn/awsui-components-core';
import FormField from '@amzn/awsui-components-core/form-field';
import Multiselect from '@amzn/awsui-components-core/multiselect';
import React, { useEffect, useState } from 'react';
import { logger } from 'src/logger';
import { getMultiSelectPlaceHolderValue } from 'src/utilities/CommonUtilities';
import { getCurrentTime, getTimeDifference } from 'src/utilities/DateTimeUtilities';
import { Account, AccountDetailsMap } from '../upgrade-medic/types';
import { useSharedFilters } from '../shared/context/SharedFiltersProvider';
import { useCostMetrics } from '../shared/context/CostMetricsProvider';
import { useDogma } from '../shared/context/DogmaProvider';
import { usePolicyEngine } from '../shared/context/PolicyEngineProvider';
import { useSas } from '../shared/context/SasProvider';
import { useTickets } from '../shared/context/TicketsProvider';
import { useUserMetrics } from '../shared/context/UserMetricsProvider';
import { useUpgradeMedic, computeUpgradeMedicDashboardData } from '../shared/context/UpgradeMedicProvider';
import { useRiskRemediator } from '../shared/context/RiskRemediatorProvider';
import { filtersLabel } from '../FinTechOpsCommonElements';
import {
  getDashboardFilters,
  stringArrayToOptionsObj,
  optionsObjectToStringArray,
  parseObjectToMultiSelectOptions_Sorted,
  buildNameToLoginMap,
  buildLoginToNameMap,
  resolveManagerNamesToAliases
} from '../FinTechOpsDataProcessingFunctions';
import { applyDashboardFiltersPolicyEngineMetrics, createOpsPolicyEngineDashboardData } from '../policy-engine/PolicyEngineDataProcessing';
import { PolicyEngineParsedDataItem } from '../policy-engine/PolicyEngineInterface';
import { applyDashboardFiltersSAS, createOpsSasData } from '../sas/SasDataProcessing';
import { SasParsedData } from '../sas/SasInterfaces';
import { applyDashboardFiltersTickets, getTicketsDashboardData } from '../tickets/TicketsDataProcessing';
import { TicketsDashboard, TicketsParsedDataItem } from '../tickets/TicketsInterfaces';
import { applyDashboardFiltersUserMetrics, createOpsUserMetricsData, returnManagerRosterData } from '../user-metrics/UserMetricsDataProcessing';
import { UserMetricsParsedData } from '../user-metrics/UserMetricsInterfaces';
import { DashboardMasterFilters, PolicyEngineDashboardItems, SaSDashboardItems, UserMetricsDashboardItems } from './FinTechOpsDataInterfaces';
import styles from './styles.module.scss';
import { applyDogmaDashboardFiltering, createOpsDogmaDashboardData } from '../dogma/DogmaDataProcessing';
import { DogmaDashboardItems, DogmaParsedData } from '../dogma/DogmaInterface';
import { applyCostMetricsDashboardFiltering, createOpsCostMetricsDashboardData } from '../cost-metrics/CostMetricsDataProcessing';
import { CostMetricsDashboardItems, CostMetricsParsedData } from '../cost-metrics/CostMetricsInterface';

interface FilterLookupMaps {
  loginToName: Map<string, string>;
  nameToLogin: Map<string, string>;
  loginToTeam: Map<string, Set<string>>;
  managerLoginSet: Set<string> | null;
  teamSet: Set<string> | null;
  assigneeLoginSet: Set<string> | null;
}

/** Build shared lookup maps for RR + UM filtering from manager data and account details. */
function buildFilterLookupMaps(
  managerData: Record<string, string>[],
  accountDetailsMap: AccountDetailsMap,
  selectedManagersList: string[],
  selectedTeamList: string[],
  selectedAssigneesList: string[],
  isManagerFilterActive: boolean,
  isTeamFilterActive: boolean,
  isAssigneeFilterActive: boolean
): FilterLookupMaps {
  const loginToName = buildLoginToNameMap(managerData);
  const nameToLogin = buildNameToLoginMap(managerData);

  const loginToTeam = new Map<string, Set<string>>();
  accountDetailsMap.forEach((details) => {
    if (!details.team) return;
    const hierarchy = (details.leadershipHierarchy || '')
      .split(',')
      .map((s: string) => s.trim())
      .filter(Boolean);
    hierarchy.forEach((alias: string) => {
      if (!loginToTeam.has(alias)) loginToTeam.set(alias, new Set());
      loginToTeam.get(alias)!.add(details.team!);
    });
    (details.owner || '')
      .split(',')
      .map((s: string) => s.trim())
      .filter(Boolean)
      .forEach((ownerName: string) => {
        const ownerLogin = nameToLogin.get(ownerName) || ownerName;
        if (!loginToTeam.has(ownerLogin)) loginToTeam.set(ownerLogin, new Set());
        loginToTeam.get(ownerLogin)!.add(details.team!);
      });
  });

  const managerLoginSet = isManagerFilterActive ? resolveManagerNamesToAliases(selectedManagersList, nameToLogin) : null;
  const teamSet = isTeamFilterActive ? new Set(selectedTeamList) : null;
  const assigneeLoginSet = isAssigneeFilterActive ? resolveManagerNamesToAliases(selectedAssigneesList, nameToLogin) : null;

  return { loginToName, nameToLogin, loginToTeam, managerLoginSet, teamSet, assigneeLoginSet };
}

/** Filter Risk Remediator parsed risks using the shared lookup maps. */
/** Filter Risk Remediator parsed risks using the shared lookup maps. Returns filtered risk arrays. */
function filterRiskRemediatorRisks(
  parsedRisks: { open: Record<string, string>[]; remediated: Record<string, string>[]; devRemediated: Record<string, string>[] },
  maps: FilterLookupMaps
) {
  const { managerLoginSet, teamSet, assigneeLoginSet, loginToTeam } = maps;
  if (!managerLoginSet && !teamSet && !assigneeLoginSet) return parsedRisks;

  const filterRR = (arr: Record<string, string>[]) =>
    arr.filter((r) => {
      const ownerAlias = r.owner_alias || '';
      if (teamSet) {
        const teams = loginToTeam.get(ownerAlias);
        if (!teams || ![...teams].some((t) => teamSet.has(t))) return false;
      }
      if (managerLoginSet) {
        const rollup = (r.leadership_rollup?.split(',') || []).map((s) => s.trim());
        if (!rollup.some((a) => managerLoginSet.has(a))) return false;
      }
      if (assigneeLoginSet && !assigneeLoginSet.has(ownerAlias)) return false;
      return true;
    });

  return {
    open: filterRR(parsedRisks.open),
    remediated: filterRR(parsedRisks.remediated),
    devRemediated: filterRR(parsedRisks.devRemediated)
  };
}

/** Filter Upgrade Medic accounts using the shared lookup maps. */
function filterUpgradeMedicDashboard(
  accounts: Account[],
  accountDetailsMap: AccountDetailsMap,
  totalJobExecutions: number,
  maps: FilterLookupMaps,
  selectedAssigneesList: string[],
  isAssigneeFilterActive: boolean
) {
  const { managerLoginSet, teamSet, loginToName } = maps;
  if (!managerLoginSet && !teamSet && !maps.assigneeLoginSet) {
    return computeUpgradeMedicDashboardData(accounts, totalJobExecutions);
  }

  const ownerSet = isAssigneeFilterActive ? new Set(selectedAssigneesList) : null;
  const filtered = accounts.filter((account) => {
    const details = accountDetailsMap.get(account.name) || accountDetailsMap.get(account.id);
    if (!details) return false;
    if (teamSet && (!details.team || !teamSet.has(details.team))) return false;
    if (managerLoginSet) {
      const chain = (details.leadershipHierarchy || '')
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean);
      if (!chain.some((alias: string) => managerLoginSet.has(alias))) return false;
    }
    if (ownerSet) {
      const owners = (details.owner || '')
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean);
      if (!owners.some((o: string) => ownerSet.has(o) || ownerSet.has(loginToName.get(o) || o))) return false;
    }
    return true;
  });

  return computeUpgradeMedicDashboardData(filtered, totalJobExecutions);
}

export const FinTechOpsSelectionFilters: React.FC = () => {
  const sharedFilters = useSharedFilters();
  const costMetricsProvider = useCostMetrics();
  const dogmaCtx = useDogma();
  const peCtx = usePolicyEngine();
  const sasCtx = useSas();
  const ticketsCtx = useTickets();
  const umCtx = useUserMetrics();
  const upgradeMedicCtx = useUpgradeMedic();
  const rrCtx = useRiskRemediator();

  const [managers, setManagers] = useState<MultiselectProps.Options>([]);
  const [assignees, setAssignees] = useState<MultiselectProps.Options>([]);
  const [team, setTeam] = useState<MultiselectProps.Options>([]);

  const [masterFilters, setMasterFilters] = useState<DashboardMasterFilters>({} as DashboardMasterFilters);

  const [isResetFilter, setIsResetFilter] = useState(false);

  useEffect(() => {
    //Triggers call upon MultiSelectStatus data finish
    if (sharedFilters.dashboardFilterStatus == 'finished') {
      selectDashboardFiltersWithAllOptions();
      setIsResetFilter(false);
    }
  }, [sharedFilters.dashboardFilterStatus, isResetFilter]);

  //Sets all filters to select all upon initial load
  const selectDashboardFiltersWithAllOptions = () => {
    try {
      if (sharedFilters.dashboardFilterData.managers.length > 0) {
        const _team = stringArrayToOptionsObj(sharedFilters.dashboardFilterData.team_name);
        const _managers = stringArrayToOptionsObj(sharedFilters.dashboardFilterData.managers);
        const _assignees = stringArrayToOptionsObj(sharedFilters.dashboardFilterData.assignees);

        setAssignees(_assignees);
        setManagers(_managers);
        setTeam(_team);

        updateData(_team, _managers, _assignees);
      }
    } catch (error: any) {
      logger.error('Unable to update selected filters', error);
    }
  };

  const updateFilters = (
    ticketsData: TicketsParsedDataItem[],
    userMetricsData: UserMetricsParsedData[],
    sasData: SasParsedData[],
    policyEngineData: PolicyEngineParsedDataItem[],
    dogmaData: DogmaParsedData[],
    costMetricsData: CostMetricsParsedData[]
  ) => {
    const filterValues = getDashboardFilters(ticketsData, userMetricsData, sasData, policyEngineData, dogmaData, costMetricsData);
    setMasterFilters(filterValues);
    setAssignees(stringArrayToOptionsObj(filterValues.uniqueAssignees));
    setManagers(stringArrayToOptionsObj(filterValues.uniqueManagers));
    setTeam(stringArrayToOptionsObj(filterValues.uniqueTeams));
  };

  const applyRiskRemediatorFilter = (selectedManagersList: string[], selectedTeamList: string[], selectedAssigneesList: string[]) => {
    try {
      const maps = buildLookupMaps(selectedManagersList, selectedTeamList, selectedAssigneesList);
      const rrParsed = rrCtx.finTechOpsRiskRemediatorParsedRisks;
      const hasRRData = rrParsed.open.length > 0 || rrParsed.remediated.length > 0 || rrParsed.devRemediated.length > 0;
      if (hasRRData) {
        rrCtx.filterDashboardRisks(filterRiskRemediatorRisks(rrParsed, maps));
      }
    } catch (e: any) {
      logger.error('Unable to update Risk Remediator dashboard data', e);
    }
  };

  const applyUpgradeMedicFilter = (selectedManagersList: string[], selectedTeamList: string[], selectedAssigneesList: string[]) => {
    try {
      const maps = buildLookupMaps(selectedManagersList, selectedTeamList, selectedAssigneesList);
      const umAccounts = upgradeMedicCtx.upgradeMedicAccounts;
      const umMap = upgradeMedicCtx.upgradeMedicAccountDetailsMap;
      const masterAssigneeCount = sharedFilters.dashboardFilterData.assignees?.length || 0;
      const isAssigneeFilterActive = masterAssigneeCount > 0 && selectedAssigneesList.length < masterAssigneeCount;
      if (umAccounts.length > 0 && umMap.size > 0) {
        upgradeMedicCtx.setUpgradeMedicDashboardData(
          filterUpgradeMedicDashboard(
            umAccounts,
            umMap,
            upgradeMedicCtx.upgradeMedicTotalJobExecutions,
            maps,
            selectedAssigneesList,
            isAssigneeFilterActive
          )
        );
      }
    } catch (e: any) {
      logger.error('Unable to update Upgrade Medic dashboard data', e);
    }
  };

  const buildLookupMaps = (selectedManagersList: string[], selectedTeamList: string[], selectedAssigneesList: string[]) => {
    const masterManagerCount = sharedFilters.dashboardFilterData.managers?.length || 0;
    const masterAssigneeCount = sharedFilters.dashboardFilterData.assignees?.length || 0;
    const masterTeamCount = sharedFilters.dashboardFilterData.team_name?.length || 0;
    return buildFilterLookupMaps(
      rrCtx.finTechOpsRemediatorManagerData || [],
      upgradeMedicCtx.upgradeMedicAccountDetailsMap,
      selectedManagersList,
      selectedTeamList,
      selectedAssigneesList,
      masterManagerCount > 0 && selectedManagersList.length < masterManagerCount,
      masterTeamCount > 0 && selectedTeamList.length < masterTeamCount,
      masterAssigneeCount > 0 && selectedAssigneesList.length < masterAssigneeCount
    );
  };

  const updateData = (_team: MultiselectProps.Options, _managers: MultiselectProps.Options, _assignees: MultiselectProps.Options) => {
    try {
      const selectedTeamList: string[] = optionsObjectToStringArray(_team);
      const selectedManagersList: string[] = optionsObjectToStringArray(_managers);
      const selectedAssigneesList: string[] = optionsObjectToStringArray(_assignees);

      const start = getCurrentTime();

      const appliedTicketsFilterData: TicketsParsedDataItem[] = applyDashboardFiltersTickets(ticketsCtx.finTechOpsTicketsData.data, {
        team_name: selectedTeamList,
        managers: selectedManagersList,
        assignees: selectedAssigneesList
      });
      const calculatedDataTickets: TicketsDashboard = getTicketsDashboardData(appliedTicketsFilterData);
      ticketsCtx.setFinTechOpsTicketDashboard(calculatedDataTickets);

      // User Metrics — exclude Manager Roster records (only needed for User Metrics hierarchy views)
      const [, nonRosterData] = returnManagerRosterData(umCtx.userMetricsWithUserData.data || []);
      const appliedUserMetricsFilterData: UserMetricsParsedData[] = applyDashboardFiltersUserMetrics(nonRosterData, {
        team_name: selectedTeamList,
        managers: selectedManagersList,
        assignees: selectedAssigneesList
      });
      const calculatedDataUserMetrics: UserMetricsDashboardItems = createOpsUserMetricsData(appliedUserMetricsFilterData);
      umCtx.setFinTechOpsUserMetricsDashBoard(calculatedDataUserMetrics);

      // SAS
      const appliedSasFilterData: SasParsedData[] = applyDashboardFiltersSAS(sasCtx.sasData.data, {
        team_name: selectedTeamList,
        managers: selectedManagersList,
        assignees: selectedAssigneesList
      });
      const calculatedDataSas: SaSDashboardItems = createOpsSasData(appliedSasFilterData);
      sasCtx.setFinTechOpsSasDashBoard(calculatedDataSas);

      const appliedPolicyEngineFilterData: PolicyEngineParsedDataItem[] = applyDashboardFiltersPolicyEngineMetrics(
        peCtx.finTechOpsPolicyEngineParsedData.data,
        {
          team_name: selectedTeamList,
          managers: selectedManagersList,
          assignees: selectedAssigneesList
        }
      );
      const calculatedDataPolicyEngine: PolicyEngineDashboardItems = createOpsPolicyEngineDashboardData(appliedPolicyEngineFilterData);
      peCtx.setFinTechOpsPolicyEngineDashboardData(calculatedDataPolicyEngine);

      const appliedDogmaFilterData: DogmaParsedData[] = applyDogmaDashboardFiltering(dogmaCtx.finTechOpsDogmaParsedData.data, {
        team_name: selectedTeamList,
        managers: selectedManagersList,
        assignees: selectedAssigneesList
      });
      const calculatedDogmaData: DogmaDashboardItems = createOpsDogmaDashboardData(appliedDogmaFilterData);
      dogmaCtx.setFinTechOpsDogmaDashboardData(calculatedDogmaData);

      // Cost Metrics
      const appliedCostMetricsFilterData: CostMetricsParsedData[] = applyCostMetricsDashboardFiltering(
        costMetricsProvider.finTechOpsCostMetricsParsedData.data,
        {
          team_name: selectedTeamList,
          managers: selectedManagersList,
          assignees: selectedAssigneesList
        }
      );
      const costMetricsMonthList = costMetricsProvider.finTechOpsCostMetricsMonthList;
      const calculatedDataCostMetrics: CostMetricsDashboardItems = createOpsCostMetricsDashboardData(
        appliedCostMetricsFilterData,
        costMetricsMonthList
      );
      costMetricsProvider.setFinTechOpsCostMetricsDashboardData(calculatedDataCostMetrics);

      applyRiskRemediatorFilter(selectedManagersList, selectedTeamList, selectedAssigneesList);
      applyUpgradeMedicFilter(selectedManagersList, selectedTeamList, selectedAssigneesList);

      updateFilters(
        appliedTicketsFilterData,
        appliedUserMetricsFilterData,
        appliedSasFilterData,
        appliedPolicyEngineFilterData,
        appliedDogmaFilterData,
        appliedCostMetricsFilterData
      );

      const elapsed = getTimeDifference(start);
      logger.info(`Calculated dashboard data for FinOps in ${elapsed} ms`);

      ticketsCtx.setFinTechOpsTicketDataStatus('finished');
      sharedFilters.setDashboardFilterStatus('finished');
      umCtx.setFinTechOpsUserMetricsDataStatus('finished');
      sasCtx.setFinTechOpsSasDataStatus('finished');
      dogmaCtx.setFinTechOpsDogmaDataStatus('finished');
      peCtx.setFinTechOpsPolicyEngineDataStatus('finished');
      costMetricsProvider.setFinTechOpsCostMetricsDataStatus('finished');
    } catch (error: any) {
      logger.error('Unable to update data for Ops Page', error);
      ticketsCtx.setFinTechOpsTicketDataStatus('error');
      sharedFilters.setDashboardFilterStatus('error');
      umCtx.setFinTechOpsUserMetricsDataStatus('error');
      sasCtx.setFinTechOpsSasDataStatus('error');
      dogmaCtx.setFinTechOpsDogmaDataStatus('error');
      peCtx.setFinTechOpsPolicyEngineDataStatus('error');
      costMetricsProvider.setFinTechOpsCostMetricsDataStatus('error');
    }
  };

  //Determines if API should be called based on selection change
  const triggerCall = () => {
    sharedFilters.setDashboardFilterStatus('loading');
    updateData(team, managers, assignees);
  };

  return (
    <Container>
      <div className={styles.multiselectContainer}>
        <div className={styles.multiselect}>
          <FormField label={filtersLabel('Teams')}>
            <Multiselect
              placeholder={getMultiSelectPlaceHolderValue(team, 'Teams', sharedFilters.dashboardFilterData.team_name)}
              options={parseObjectToMultiSelectOptions_Sorted(masterFilters.uniqueTeams, 'All Teams')}
              selectedOptions={team}
              onChange={({ detail }) => setTeam(detail.selectedOptions)}
              onBlur={({ detail }) => triggerCall()}
              deselectAriaLabel={(e) => `Remove ${e.label}`}
              selectedAriaLabel="Selected"
              expandToViewport
              hideTokens
              virtualScroll
              filteringType="auto"
              loadingText="Loading Teams"
              errorText="Unable to load data"
              statusType={sharedFilters.dashboardFilterStatus}
              disabled={sharedFilters.dashboardFilterDataCalculationStatus === 'loading'}
            />
          </FormField>
        </div>
        <div className={styles.multiselect}>
          <FormField label={filtersLabel('Managers')}>
            <Multiselect
              placeholder={getMultiSelectPlaceHolderValue(managers, 'Managers', sharedFilters.dashboardFilterData.managers)}
              options={parseObjectToMultiSelectOptions_Sorted(masterFilters.uniqueManagers, 'All Managers')}
              selectedOptions={managers}
              onChange={({ detail }) => setManagers(detail.selectedOptions)}
              onBlur={({ detail }) => triggerCall()}
              deselectAriaLabel={(e) => `Remove ${e.label}`}
              selectedAriaLabel="Selected"
              expandToViewport
              hideTokens
              virtualScroll
              filteringType="auto"
              loadingText="Loading Managers"
              errorText="Unable to load data"
              statusType={sharedFilters.dashboardFilterStatus}
              disabled={sharedFilters.dashboardFilterDataCalculationStatus === 'loading'}
            />
          </FormField>
        </div>
        <div className={styles.multiselect}>
          <FormField label={filtersLabel('Assignees')}>
            <Multiselect
              placeholder={getMultiSelectPlaceHolderValue(assignees, 'Assignees', sharedFilters.dashboardFilterData.assignees)}
              options={parseObjectToMultiSelectOptions_Sorted(masterFilters.uniqueAssignees, 'All Assignees')}
              selectedOptions={assignees}
              onChange={({ detail }) => setAssignees(detail.selectedOptions)}
              onBlur={({ detail }) => triggerCall()}
              deselectAriaLabel={(e) => `Remove ${e.label}`}
              selectedAriaLabel="Selected"
              expandToViewport
              hideTokens
              virtualScroll
              filteringType="auto"
              loadingText="Loading Assignees"
              errorText="Unable to load data"
              statusType={sharedFilters.dashboardFilterStatus}
              disabled={sharedFilters.dashboardFilterDataCalculationStatus === 'loading'}
            />
          </FormField>
        </div>
        <div style={{ alignSelf: 'flex-end' }}>
          <Button onClick={() => setIsResetFilter(true)}>Clear Filters</Button>
        </div>
      </div>
    </Container>
  );
};
